import * as React from "react";
import { cn } from "@/lib/utils";

export const Table = React.forwardRef<
  HTMLTableElement,
  React.TableHTMLAttributes<HTMLTableElement>
>(({ className, ...rest }, ref) => (
  <table ref={ref} className={cn("w-full border-collapse text-[13px]", className)} {...rest} />
));
Table.displayName = "Table";

export const TableHead = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...rest }, ref) => <thead ref={ref} className={cn("", className)} {...rest} />);
TableHead.displayName = "TableHead";

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...rest }, ref) => <tbody ref={ref} className={cn("", className)} {...rest} />);
TableBody.displayName = "TableBody";

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...rest }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "[&_td]:border-b [&_td]:border-border-subtle [&_td]:px-3 [&_td]:py-3",
      "[&_td]:align-middle [&_td]:text-text-primary",
      // ⚠️ `[&:hover_td]`, não `hover:[&_td]`: o segundo compila para `.linha td:hover` e acende
      // só a célula sob o cursor. Numa grade de conferência número a número, destacar a célula em
      // vez da linha é pior que não destacar nada — o olho perde a linha que estava seguindo.
      "transition-colors [&:hover_td]:bg-bg-muted-rd",
      // Mesma armadilha, e ela custou a borda da última COLUNA em toda tabela do app:
      // `last:[&_td]` compila para `.linha td:last-child` (a última célula de cada linha), não para
      // as células da última linha. As linhas paravam uma coluna antes do fim.
      "[&:last-child_td]:border-b-0",
      className
    )}
    {...rest}
  />
));
TableRow.displayName = "TableRow";

export const TableHeaderCell = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...rest }, ref) => (
  <th
    ref={ref}
    className={cn(
      "px-3 py-2.5 text-left",
      "text-[11px] font-semibold uppercase tracking-widest",
      "border-b border-border-subtle bg-bg-muted-rd text-text-tertiary",
      className
    )}
    {...rest}
  />
));
TableHeaderCell.displayName = "TableHeaderCell";

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...rest }, ref) => <td ref={ref} className={cn(className)} {...rest} />);
TableCell.displayName = "TableCell";

export interface TableSkeletonProps {
  columns: number;
  rows?: number;
  className?: string;
}

export function TableSkeleton({
  columns,
  rows = 5,
  className,
}: TableSkeletonProps): React.ReactElement {
  return (
    <Table className={className} aria-hidden="true">
      <TableHead>
        <tr>
          {Array.from({ length: columns }).map((_, i) => (
            <TableHeaderCell key={i}>
              <span className="bg-bg-muted inline-block h-2.5 w-14 rounded motion-safe:animate-pulse-dot" />
            </TableHeaderCell>
          ))}
        </tr>
      </TableHead>
      <TableBody>
        {Array.from({ length: rows }).map((_, r) => (
          <TableRow key={r}>
            {Array.from({ length: columns }).map((_, c) => (
              <TableCell key={c}>
                <span
                  className="bg-bg-muted inline-block h-3 w-[var(--skel-w)] rounded motion-safe:animate-pulse-dot"
                  style={{ "--skel-w": `${40 + (((r + c) * 7) % 50)}%` } as React.CSSProperties}
                />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
